import * as StateLib from "@/Utils/StateLib";
import { isNullish } from "@theorgchartdev/storybook";
import { sanitizeBlockedValue } from "@coreapp/utils/FormatFieldValue";
import {
  enumOverrideType,
  enumCellObjectSubtypes,
  kPositionStatusID,
  kEmployeeAssignmentStatusID
} from "@coreapp/constants";
import { GetObjClassForOverride } from "@coreapp/utils/GetObjClassForOverride";

// See QRevertManagerPanel.InitConflictFields
// Want to track conflicting fields across the override as a whole, not just per change.
// TODO: This can be cleaned up.
export const GetChangeFieldReverts = overrideObj => {
  const wsMgr = StateLib.GetWSMgr();
  const planMgr = wsMgr?.GetPlanMgr();
  if (isNullish(planMgr)) {
    return {};
  }

  const changeFieldReverts = {};
  const allActiveChanges = planMgr.allActiveChanges(overrideObj);

  for (const aChangeIdx in allActiveChanges) {
    // new -> old
    const chgObject = allActiveChanges[aChangeIdx];
    if (chgObject.fConflict) {
      // Do we have conflicting fields in this change?
      let conflictFields = [];
      if (chgObject.fConflictFields) {
        conflictFields = chgObject.fConflictFields;
      } else if (chgObject.fDelta) {
        conflictFields = Object.keys(chgObject.fDelta);
      }

      for (const anIdx in conflictFields) {
        const aField = conflictFields[anIdx];
        changeFieldReverts[aField] = false;
      }
    }
  }

  return changeFieldReverts;
};

export const ConflictedRecordExists = (overrideObj, dbObj) => {
  const wsMgr = StateLib.GetWSMgr();
  const planMgr = wsMgr?.GetPlanMgr();
  if (isNullish(planMgr) || isNullish(overrideObj)) {
    return false;
  }

  const chgObject = overrideObj.lastActiveChange();
  return chgObject && !isNullish(dbObj); // record that was changed
};

export const GetChangeFieldRevertInfo = ({
  field,
  dbObj,
  overrideObj,
  planSupervisor,
  sourceSupervisor
}) => {
  const wsMgr = StateLib.GetWSMgr();
  const planMgr = wsMgr?.GetPlanMgr();
  const doc = planMgr?.GetDoc();
  if (isNullish(doc)) {
    return {};
  }

  const objClass = GetObjClassForOverride(overrideObj, doc);
  const supervisorField = objClass?.GetParentIDFieldDef()?.GetFieldName() ?? "";

  const chgObject = overrideObj.lastActiveChange();
  const recExists = ConflictedRecordExists(overrideObj, dbObj);

  const allActiveChanges = planMgr.allActiveChanges(overrideObj);
  const changeValue = planMgr.currentActiveValue(field, allActiveChanges);
  const fieldDef = objClass.GetFieldDefByName(field);
  const planDBObj = planSupervisor; // plan supervisor
  const sourceDBObj = sourceSupervisor; // source supervisor

  let invalidPlanSuper = false;
  let invalidSourceSuper = false;
  let missingRec = false;
  let planData = "<Removed>";
  let sourceData = "<Removed>";
  if (fieldDef && changeValue && dbObj) {
    const fieldName = fieldDef.GetFieldName();
    chgObject.fDBObj = dbObj;
    if (dbObj.fCloneBase) {
      sourceData = chgObject.GetOriginalValue(fieldDef);
    } else {
      sourceData = "<Loading>";
    }

    planData = changeValue;
    // SupervisorID changes are a unique case. Invalid new/old super means a rec has been removed.
    if (fieldName === supervisorField) {
      if (!planDBObj) {
        invalidPlanSuper = true;
        missingRec = true;
        planData = "<Removed>";
      } else {
        planData =
          sanitizeBlockedValue(planDBObj.SmartGetFieldValue("Name")) +
          " (" +
          sanitizeBlockedValue(planDBObj.GetObjectRecordID()) +
          ")";

        if (planDBObj.IsPositionObj()) {
          planData = " (" + sanitizeBlockedValue(planDBObj.GetObjectRecordID()) + ")";
        }
      }

      if (!sourceDBObj) {
        invalidSourceSuper = true;
        missingRec = true;
        sourceData = "<Removed>";
      } else {
        sourceData =
          sanitizeBlockedValue(sourceDBObj.SmartGetFieldValue("Name")) +
          " (" +
          sanitizeBlockedValue(sourceDBObj.GetObjectRecordID()) +
          ")";

        if (sourceDBObj.IsPositionObj()) {
          sourceData = " (" + sanitizeBlockedValue(sourceDBObj.GetObjectRecordID()) + ")";
        }
      }
    }
  }

  return {
    invalidPlanSuper: invalidPlanSuper,
    invalidSourceSuper: invalidSourceSuper,
    sourceData: sourceData,
    planData: planData,
    missingRec: missingRec,
    fieldDef: fieldDef,
    planDBObj: planDBObj,
    sourceDBObj: sourceDBObj,
    recExists: recExists,
    changeValue: changeValue
  };
};

export const ValidateRevertField = function (props) {
  var changeRevertInfo = GetChangeFieldRevertInfo(props);
  var invalidPlanSuper = changeRevertInfo["invalidPlanSuper"];
  var invalidSourceSuper = changeRevertInfo["invalidSourceSuper"];
  var missingRec = changeRevertInfo["missingRec"];
  var recExists = changeRevertInfo["recExists"];

  var isValidSourceField = false;
  var isValidPlanField = false;
  var isValidPickList = false;
  if (!missingRec && !invalidSourceSuper && recExists) {
    var fieldDef = changeRevertInfo["fieldDef"];
    var isPickListField = fieldDef.GetFieldSubtype() === enumCellObjectSubtypes.picklist;
    if (isPickListField) {
      var pickValues = fieldDef.GetPickValues();
      var sourceData = changeRevertInfo["sourceData"];
      var planData = changeRevertInfo["planData"];
      if (pickValues.indexOf(planData) !== -1) {
        isValidPickList = true;
      }

      var sourceDBObj = changeRevertInfo["sourceDBObj"];
      if (sourceDBObj && pickValues.indexOf(sourceData) !== -1) {
        isValidSourceField = true;
      }
    } else {
      if (!invalidSourceSuper) {
        isValidSourceField = true;
      }
      if (!invalidPlanSuper) {
        isValidPlanField = true;
      }
    }
  }
  return {
    validSourceField: isValidSourceField,
    validPlanField: isValidPlanField,
    validPickList: isValidPickList
  };
};

export const GetEmployeeLabel = (dbObj, overrideObj) => {
  if (dbObj) {
    return dbObj.FormatEmployeeLabel();
  }

  const displayObj = overrideObj?.SmartGetFieldValue("displayObject");
  if (!isNullish(displayObj)) {
    return displayObj.FormatEmployeeLabel();
  }

  return "";
};

export const SendUpdate = (overrideObj, revertAll) => {
  const wsMgr = StateLib.GetWSMgr();
  const planMgr = wsMgr?.GetPlanMgr();
  if (isNullish(planMgr)) {
    return;
  }

  return planMgr.SendUpdate(overrideObj, false, false, revertAll);
};

// See "Resolve" within QRvertManagerPanel
export const Resolve = (dbObj, overrideObj, changeFieldReverts) => {
  const wsMgr = StateLib.GetWSMgr();
  const planMgr = wsMgr?.GetPlanMgr();
  if (isNullish(planMgr)) {
    return;
  }

  let revert = false;
  const allActiveChanges = planMgr.allActiveChanges(overrideObj);

  for (const changeIdx in allActiveChanges) {
    const curChange = allActiveChanges[changeIdx];
    for (const aField in changeFieldReverts) {
      const discardVal = changeFieldReverts[aField];
      if (discardVal) {
        delete curChange.fDelta[aField];
      }
    }
    revert = ValidateOverrideType(curChange, dbObj, overrideObj);
    curChange.fConflict = false;
    if (curChange.fIsReverted === false) {
      let nextDeltaFields = Object.keys(curChange.fDelta);
      curChange.fIsReverted = nextDeltaFields.length === 0;
    }
  }

  return revert;
};

// See "doRevert" within QRvertManagerPanel
// DIFFERENT THAN REVERTNG 1 at a time! This one monolithically reverts them all.
export const Revert = (dbObj, overrideObj) => {
  const changeList = overrideObj.fChanges;
  let isValidRevert = true;
  for (var i = changeList.length; i > 0; i--) {
    const theChange = changeList[i - 1];
    if (theChange.IsReverted() !== true) {
      theChange.fIsReverted = true;
    }
    isValidRevert = isValidRevert && ValidateOverrideType(theChange, dbObj, overrideObj, true);
  }

  return isValidRevert;
};

/*
 * This method will ensure that the override is still valid, if not it will
 * change the override type to the correct type it should be. If there are no more
 * valid changes, then the override will be reverted completely.
 */
export const ValidateOverrideType = (curChange, dbObj, overrideObj, isRevert = false) => {
  switch (curChange.fChangeType) {
    case enumOverrideType.move:
      return ValidateMove(curChange, dbObj, overrideObj, isRevert);
    default:
      return true;
  }
};

/*
 * curChange.fConflictFields contains list of fieldDefNames (not IDs), so the name for SupervisorID must be retrieved
 * from the dbObj for first if condition.
 * Specific handling for Move & all possible Move cases. (Supports Conflict Resolving & Reverting)
 * All possible cases are:
 * @Case1 A now invalid Move containing a valid 'Change' (Override type to become 'Change'),
 * @Case2 A now invalid Move containing a valid 'Comment' (Override type to become 'Comment'),
 * @Case3 A reverted move w/ no other changes (Override should be reverted),
 * @Case4 A valid move after resolving conflict (Override will persist).
 * @param isRevert True/False
 */
export const ValidateMove = (curChange, dbObj, overrideObj, isRevert = false) => {
  const conflictFields = curChange.fConflictFields ? Object.values(curChange.fConflictFields) : [];
  const deltaFields = Object.keys(curChange.fDelta);
  // Check if there are other deltaFields besides just the supervisorID
  if (isRevert) {
    curChange.fIsReverted = false;
  }

  const parentClass = dbObj?.GetParentClass();
  const parentIDDef = parentClass?.GetParentIDFieldDef();
  const parentIDName = parentIDDef?.GetFieldName() || "SupervisorID";

  if (
    conflictFields.includes(parentIDName) &&
    deltaFields.length > 0 &&
    !deltaFields.includes(parentIDName)
  ) {
    curChange.fChangeType = enumOverrideType.change;
    overrideObj.SmartSetFieldValue("Type", enumOverrideType.change);
    curChange.fConflictFields = deltaFields;
  } else {
    const onlyParentChanged = (() => {
      const parentFields = [parentIDName];
      if (dbObj.IsPositionObj()) {
        parentFields.push(kPositionStatusID);
      } else {
        parentFields.push(kEmployeeAssignmentStatusID);
      }

      return deltaFields.every(field => parentFields.includes(field));
    })();

    if (!onlyParentChanged) {
      curChange.fChangeType = enumOverrideType.change;
      overrideObj.SmartSetFieldValue("Type", enumOverrideType.change);
      curChange.fConflictFields = deltaFields;
    } else if (
      curChange.fParentOverride &&
      curChange.fParentOverride.SmartGetFieldValue("Comment")
    ) {
      curChange.fChangeType = enumOverrideType.comment;
      overrideObj.SmartSetFieldValue("Type", enumOverrideType.comment);
    } else {
      // Can do a revert at this point because there is no other outstanding change than the now invalidated supervisorID diff
      if (isRevert) {
        curChange.fIsReverted = true;
      }

      return true;
    }
  }

  return false;
};
